// @flow

import * as React from 'react';
import { OutlinedInput, List, ListItem } from '@material-ui/core';
import { useCombobox } from 'downshift';
import { useLocale } from '../hooks/locale.js';
import { InputPopup } from '../controls/popup.js';

type SelectProps = {|
  // changing on every render will trigger controlled selectedItem change
  items: $ReadOnlyArray<{| id: string, label: ?string |}>,
  value: ?string,
  onSelect: (?string) => void,
  onBlur?: () => void,
|};

const filterItems = (items, inputValue) => {
  const lowercased = inputValue.toLowerCase();
  if (inputValue === '') {
    return items;
  }
  return items.filter(
    item => item.label != null && item.label.toLowerCase().includes(lowercased),
  );
};

// apply selected item label when input looses focus
const reduceSelectState = (state, action) => {
  switch (action.type) {
    case useCombobox.stateChangeTypes.InputBlur:
      return {
        ...action.changes,
        inputValue: action.props.itemToString?.(action.changes.selectedItem),
      };
    default:
      return action.changes;
  }
};

// optimise rendering big lists like nationalities
const SelectItem = React.memo(({ getItemProps, index, item, selected }) => {
  return (
    <ListItem {...getItemProps({ index, item, button: true, selected })}>
      {item.label}
    </ListItem>
  );
});

export const SelectInput = (props: SelectProps): React.Node => {
  const { t } = useLocale();
  const { value, items } = props;
  const [filtered, dispatchFilter] = React.useReducer(
    (state, inputValue) => filterItems(items, inputValue),
    items,
  );
  const inputRef = React.useRef(null);
  const combobox = useCombobox({
    items: filtered,
    stateReducer: reduceSelectState,
    // make selectedItem always controlled by coalescing null
    selectedItem: items.find(item => item.id === value) ?? null,
    itemToString: item => item?.label ?? '',
    onInputValueChange: ({ inputValue }) => dispatchFilter(inputValue),
    onSelectedItemChange: ({ selectedItem }) =>
      props.onSelect(selectedItem?.id),
  });
  const { getComboboxProps } = combobox;
  const { getInputProps, getMenuProps, getItemProps } = combobox;
  const { inputValue, isOpen, highlightedIndex } = combobox;
  const { openMenu } = combobox;
  return (
    <div {...getComboboxProps()}>
      <OutlinedInput
        {...getInputProps({
          ref: inputRef,
          placeholder: t('notSpecified'),
          onFocus: () => {
            if (isOpen === false) {
              dispatchFilter(inputValue);
              openMenu();
            }
          },
          onBlur: props.onBlur,
        })}
      />
      <InputPopup referenceRef={inputRef} open={isOpen}>
        <List {...getMenuProps()}>
          {filtered.length === 0 && (
            <ListItem disabled={true}>{t('noOptions')}</ListItem>
          )}
          {isOpen &&
            filtered.map((item, index) => (
              <SelectItem
                key={item.id}
                getItemProps={getItemProps}
                index={index}
                item={item}
                selected={highlightedIndex === index}
              />
            ))}
        </List>
      </InputPopup>
    </div>
  );
};
